import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { find } from "ramda";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  fetchDeviceMe,
  fetchDevicesByCompanyUuid,
  getUrlSerenity,
  getUrlVigebox,
} from "../../../api/device";
import { enumsectionToI18nKey } from "../../../generated/enums";
import {
  DeviceWithoutLegacyPassword,
  DeviceWithoutLegacyPasswordList2,
} from "../../../generated/types";
import { useAccountStorage } from "../../../hooks/useAccountStorage";
import { LayoutMode, useLayoutMode } from "../../../hooks/useLayoutMode";
import { spaceForType } from "../../../utils/roles";
import { Header } from "../../Header";
import { sectionToIcon } from "../../type/default";
import { EmptyFramePlaceholder, FrameDevice } from "./FrameDevice";

// L'écran distant se dimensionne sur la place qu'on lui laisse : la page occupe donc toute
// la hauteur visible (`dvh` suit la barre d'adresse mobile), moins les 4rem que le layout
// réserve à la navbar du bas quand elle est affichée — sinon la page dépasse et défile.
const pageClass = (layout: LayoutMode) =>
  `flex flex-col ${
    layout === "mobile" ? "h-[calc(100dvh-4rem)]" : "h-[100dvh]"
  } bg-grey-dark md:bg-white`;

// Écart entre les cases du mode split, en pixels.
const SLOT_GAP = 8;

/**
 * Position d'une case. Vue individuelle : la case 0 occupe toute la zone. Split : grille 2×2,
 * la case 0 en haut à gauche. Positions en absolu pour que le passage d'un mode à l'autre
 * s'anime (une grille CSS ne s'anime pas) — et surtout pour que la case 0 reste le MÊME
 * élément : son écran n'est jamais rechargé, sa connexion reste ouverte.
 */
export const slotStyle = (index: number, split: boolean): CSSProperties => {
  if (!split) return { left: 0, top: 0, width: "100%", height: "100%" };
  const half = `calc(50% - ${SLOT_GAP / 2}px)`;
  const far = `calc(50% + ${SLOT_GAP / 2}px)`;
  return {
    left: index % 2 ? far : 0,
    top: index < 2 ? 0 : far,
    width: half,
    height: half,
  };
};

/** Produits triés par nom (l'uuid à défaut) : un ordre stable pour les listes et le split. */
export const sortDevices = (
  devices: DeviceWithoutLegacyPassword[]
): DeviceWithoutLegacyPassword[] =>
  [...devices].sort((a, b) =>
    (a.name || a.uuid).localeCompare(b.name || b.uuid)
  );

type Others = (string | undefined)[];

/** Cases 1 à 3 du split à la première ouverture : les produits suivants, sans le principal. */
export const defaultOthers = (
  sorted: DeviceWithoutLegacyPassword[],
  activeUuid: string | undefined
): Others => {
  const uuids = sorted.map((d) => d.uuid).filter((u) => u !== activeUuid);
  return [0, 1, 2].map((i) => uuids.at(i));
};

/**
 * Le produit de la case `index` (1 à 3) passe en principal, l'ancien principal prend sa
 * place : chaque produit reste affiché une seule fois.
 */
export const promoteSlot = (
  others: Others,
  index: number,
  activeUuid: string | undefined
): { active: string | undefined; others: Others } => {
  const next = [...others];
  const active = next[index - 1];
  next[index - 1] = activeUuid;
  return { active, others: next };
};

/** Nouveau principal choisi dans la liste : s'il occupait une case, l'ancien principal la prend. */
export const swapIfTaken = (
  others: Others,
  newActive: string,
  oldActive: string | undefined
): Others => others.map((u) => (u === newActive ? oldActive : u));

/** Lien de connexion demandé à chaque apparition de l'écran : un lien noVNC meurt après 60 s. */
const useRemoteLink = (device: DeviceWithoutLegacyPassword) => {
  const [link, setLink] = useState<{ url?: string; error?: string }>({});
  useEffect(() => {
    let alive = true;
    setLink({});
    (device.type === "serenity" ? getUrlSerenity : getUrlVigebox)(device).then(
      ({ url, error }) => {
        if (alive) setLink(error ? { error } : { url });
      }
    );
    return () => {
      alive = false;
    };
    // Un nouveau lien par produit affiché, pas à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.uuid]);
  return link;
};

const RemoteScreen = ({ device }: { device: DeviceWithoutLegacyPassword }) => {
  const { t } = useTranslation();
  const { url, error } = useRemoteLink(device);
  if (error) {
    return (
      <div className="text-red-500 text-sm px-2 py-3">
        <span className="font-semibold">⚠️ {t("remote-view-error")} </span>
        {t(error)}
        <p className="text-xs text-grey-medium mt-2">
          {t("remote-view-error-retry")}
        </p>
      </div>
    );
  }
  if (!url) {
    return (
      <div className="text-grey-medium text-sm px-2 py-1">
        {t("remote-view-stream-loading")}
      </div>
    );
  }
  // `&scale=true` dans les deux modes : l'adresse de l'écran ne doit pas changer d'un mode à
  // l'autre, sinon le navigateur le recharge et la connexion est perdue.
  return <FrameDevice url={url + "&scale=true"} name={device.name} />;
};

type SlotProps = {
  index: number;
  device: DeviceWithoutLegacyPassword | undefined;
  devices: DeviceWithoutLegacyPassword[];
  takenUuids: (string | undefined)[];
  split: boolean;
  canSplit: boolean;
  showToolbar: boolean;
  onSelect: (index: number, uuid: string) => void;
  onToggle: (index: number) => void;
};

const Slot = ({
  index,
  device,
  devices,
  takenUuids,
  split,
  canSplit,
  showToolbar,
  onSelect,
  onToggle,
}: SlotProps) => {
  const { t } = useTranslation();
  return (
    <div
      style={slotStyle(index, split)}
      className={`absolute flex flex-col min-h-0 min-w-0 bg-grey-dark text-secondary-text rounded-lg border border-grey-light overflow-hidden transition-[top,left,width,height] duration-300 ease-out motion-reduce:transition-none ${
        index > 0 ? "animate-remote-slot-in motion-reduce:animate-none" : ""
      }`}
    >
      {showToolbar && (
        <div className="px-2 py-1 flex items-center gap-2 text-lg border-b border-grey-light">
          {split ? (
            <button
              onClick={() => onToggle(index)}
              className="text-secondary-text hover:text-secondary transition-colors"
              title={t("remote-view-single-mode")}
            >
              <FontAwesomeIcon icon="expand" />
            </button>
          ) : (
            canSplit && (
              <button
                onClick={() => onToggle(index)}
                className="hidden md:block text-secondary-text hover:text-secondary transition-colors"
                title={t("remote-view-split-mode")}
              >
                <FontAwesomeIcon icon="th-large" />
              </button>
            )
          )}
          {devices.length > 0 && (
            <select
              className="flex-1 min-w-0 border border-grey-light rounded px-2 py-0.5 text-base bg-grey-dark text-secondary-text"
              value={device?.uuid ?? ""}
              onChange={(e) => onSelect(index, e.target.value)}
            >
              {index > 0 && (
                <option value="">{t("remote-view-slot-none")}</option>
              )}
              {devices.map((d) => (
                <option
                  key={d.uuid}
                  value={d.uuid}
                  // Un produit n'est affiché qu'une fois : déjà dans une autre case → grisé.
                  disabled={
                    d.uuid !== device?.uuid && takenUuids.includes(d.uuid)
                  }
                >
                  {d.name || d.uuid}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
      {device ? (
        <RemoteScreen device={device} />
      ) : split ? (
        <EmptyFramePlaceholder />
      ) : (
        <div className="text-grey-medium text-sm px-2 py-1">
          {t("remote-view-unavailable")}
        </div>
      )}
    </div>
  );
};

type ViewProps = {
  devices: DeviceWithoutLegacyPasswordList2 | undefined;
  activeDevice: DeviceWithoutLegacyPassword | undefined;
  setActiveDevice: (device: DeviceWithoutLegacyPassword) => void;
};

/**
 * Vue à distance : le produit principal seul, ou jusqu'à 4 produits (split, espace admin,
 * écran large). Le principal est toujours la case 0, en haut à gauche, et garde sa connexion
 * d'un mode à l'autre. En vue individuelle, les autres cases sont retirées : leurs
 * connexions sont fermées, pas seulement cachées.
 */
const View = ({ devices, activeDevice, setActiveDevice }: ViewProps) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"single" | "split">("single");
  const [others, setOthers] = useState<Others>([]);
  const layout = useLayoutMode();
  // Le mode split (jusqu'à 4 produits) est réservé à l'espace admin (types admin et psc) :
  // les autres comptes n'ont que la vue individuelle, sans le bouton pour y basculer.
  const { getAccountStorage } = useAccountStorage();
  const canSplit = spaceForType(getAccountStorage()?.type) === "admin";
  const split = mode === "split" && layout === "desktop" && canSplit;
  const sorted = useMemo(() => sortDevices(devices ?? []), [devices]);
  const byUuid = (uuid: string | undefined) =>
    uuid ? sorted.find((d) => d.uuid === uuid) : undefined;

  const activeUuid = activeDevice?.uuid;
  const shown = split ? [activeUuid, ...others] : [activeUuid];

  const handleToggle = (index: number) => {
    if (!split) {
      setOthers((prev) =>
        prev.length ? prev : defaultOthers(sorted, activeUuid)
      );
      setMode("split");
      return;
    }
    if (index > 0) {
      const promoted = promoteSlot(others, index, activeUuid);
      const device = byUuid(promoted.active);
      if (!device) return; // case vide : rien à agrandir
      setOthers(promoted.others);
      setActiveDevice(device);
    }
    setMode("single");
  };

  const handleSelect = (index: number, uuid: string) => {
    if (index === 0) {
      const device = byUuid(uuid);
      if (!device) return;
      setOthers((prev) => swapIfTaken(prev, uuid, activeUuid));
      setActiveDevice(device);
      return;
    }
    setOthers((prev) =>
      prev.map((u, i) => (i === index - 1 ? uuid || undefined : u))
    );
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col md:px-6 md:pb-4">
      {split && sorted.length === 0 ? (
        <div className="text-grey-medium text-sm">
          {t("remote-view-split-no-device")}
        </div>
      ) : (
        <div className="relative flex-1 min-h-0">
          {shown.map((uuid, index) => (
            <Slot
              // La clé est le produit, pas la position : c'est ce qui garde l'écran du
              // principal (et sa connexion) quand on change de mode.
              key={uuid ?? `empty-${index}`}
              index={index}
              device={byUuid(uuid)}
              devices={sorted}
              takenUuids={shown}
              split={split}
              canSplit={canSplit}
              // Téléphone tourné : toute la hauteur pour l'écran du produit. Revenir en
              // portrait rend le choix du produit.
              showToolbar={split || layout !== "phone-landscape"}
              onSelect={handleSelect}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Produit à afficher dans la vue à distance. Avec un uuid demandé : CE produit, ou aucun.
 * Jamais un autre à sa place : se rabattre sur le premier de la liste affichait l'écran
 * d'un autre produit sous l'adresse de celui demandé, sans rien signaler.
 * Sans uuid : le premier de la liste.
 */
export const pickActiveDevice = (
  devices: DeviceWithoutLegacyPassword[],
  uuid?: string
): DeviceWithoutLegacyPassword | undefined =>
  uuid ? find((device) => device.uuid === uuid, devices) : devices[0];

/**
 * Titre de la vue distante : « <bâtiment ou régulateur> — <uuid> ».
 *
 * L'uuid est là parce que c'est l'identifiant que l'utilisateur transmet au support ; il
 * remplace l'IP locale de l'ancien titre, qui n'a de sens que sur le réseau de l'élevage.
 * Le nom du bâtiment vient de l'appelant (œil du widget) : cette page ne charge que des
 * devices. Sans lui — arrivée depuis « Produits » — on retombe sur le nom du régulateur.
 *
 * `filter(Boolean)` : l'ancien code concaténait sans garde et affichait « undefined - undefined »
 * le temps du chargement.
 */
export function remoteViewTitle(
  buildingName: string | undefined,
  device: Pick<DeviceWithoutLegacyPassword, "name" | "uuid"> | undefined
): string {
  return [buildingName || device?.name, device?.uuid]
    .filter(Boolean)
    .join(" — ");
}

export const DeviceViewUuid = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const { state } = useLocation() as {
    state?: { buildingName?: string } | null;
  };
  const [activeDevice, setActiveDevice] = useState<
    DeviceWithoutLegacyPassword | undefined
  >(undefined);
  const [devices, setDevices] = useState<
    DeviceWithoutLegacyPasswordList2 | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const layout = useLayoutMode();

  useEffect(() => {
    if (!uuid) return;
    // Pas de filtre par type : tous les types ont une connexion à distance (Serenity par
    // /link, avitouch, avibox, labelbox et vigebox par VNC). Le filtre serenity/vigebox
    // d'avant écartait les autres legacy : l'œil d'une labelbox n'affichait rien.
    fetchDevicesByCompanyUuid(uuid).then(({ devices, error }) => {
      setDevices(devices);
      setActiveDevice(pickActiveDevice(devices, uuid));
      setError(error);
      setIsLoading(false);
    });
  }, [uuid]);

  return (
    <div className={pageClass(layout)}>
      {/* Header avec bouton retour */}
      <div className="hidden md:block p-4">
        <Header
          title={
            remoteViewTitle(state?.buildingName, activeDevice) ||
            t(enumsectionToI18nKey["section_device"])
          }
          icon={sectionToIcon.section_device}
          buttonText={t("remote-view-back")}
          onButtonClick={() => navigate(-1)}
        />
      </div>

      {isLoading ? (
        <p className="p-4 text-white md:text-tertiary">{t("loading")}</p>
      ) : error ? (
        <p className="p-4 text-red-500">{error}</p>
      ) : (
        <View
          devices={devices}
          activeDevice={activeDevice}
          setActiveDevice={setActiveDevice}
        />
      )}
    </div>
  );
};

export const DeviceViewMe = () => {
  const [activeDevice, setActiveDevice] = useState<
    DeviceWithoutLegacyPassword | undefined
  >(undefined);
  const [devices, setDevices] = useState<
    DeviceWithoutLegacyPasswordList2 | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const layout = useLayoutMode();

  useEffect(() => {
    fetchDeviceMe().then(({ devices, error }) => {
      setDevices(devices);
      // Le premier de la liste telle qu'elle s'affiche (triée par nom).
      setActiveDevice(pickActiveDevice(sortDevices(devices)));
      setError(error);
      setIsLoading(false);
    });
  }, []);

  return (
    <div className={pageClass(layout)}>
      {/* Header avec bouton retour */}
      <div className="hidden md:block p-4">
        <Header
          title={t(enumsectionToI18nKey["section_my_device"])}
          icon={sectionToIcon.section_my_device}
        />
      </div>

      {isLoading ? (
        <p className="p-4 text-white md:text-tertiary">{t("loading")}</p>
      ) : error ? (
        <p className="p-4 text-red-500">{error}</p>
      ) : (
        <View
          devices={devices}
          activeDevice={activeDevice}
          setActiveDevice={setActiveDevice}
        />
      )}
    </div>
  );
};
